import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Send } from 'lucide-react';
import { offlineListings } from './offlineListingData';
import agentImage from '../assets/agent.jpg';

const WEBHOOK_URL =
    'https://script.google.com/macros/s/AKfycbzxVj62bh2xShDpx2nz74vEWpq8slPDQy8geuqP5rJ0xf4e25FMvqgmnvTeh0HGZGLBqw/exec';

function WhatsAppIcon({ className = 'h-5 w-5' }) {
    return (
        <span className={className}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 448 512"
                className="h-full w-full"
                aria-hidden="true"
            >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
            </svg>
        </span>
    );
}

function OfflineForm() {
    const navigate = useNavigate();
    const { listingId } = useParams();
    const normalizedListingId = decodeURIComponent(listingId || '');

    const [showDesktopPopup, setShowDesktopPopup] = useState(false);
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [errors, setErrors] = useState({ name: '', mobile: '' });
    const [showLiveAgentButton, setShowLiveAgentButton] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const listing = useMemo(() => {
        return offlineListings.find((item) => String(item.id) === String(normalizedListingId));
    }, [normalizedListingId]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 769px)');

        const handleScreenChange = (event) => {
            setShowDesktopPopup(event.matches);
        };

        setShowDesktopPopup(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleScreenChange);
        } else {
            mediaQuery.addListener(handleScreenChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleScreenChange);
            } else {
                mediaQuery.removeListener(handleScreenChange);
            }
        };
    }, []);

    if (!listing) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f4f5f7] px-4">
                <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 text-center shadow-[0_10px_24px_rgba(20,38,53,0.15)]">
                    <h2 className="text-xl font-semibold text-[#243445]">Listing Not Found</h2>
                    <button
                        type="button"
                        onClick={() => navigate('/offline-listing')}
                        className="mt-4 rounded-[10px] bg-[#1d85d6] px-4 py-2 text-sm font-semibold text-white"
                    >
                        Back to Listings
                    </button>
                </div>
            </div>
        );
    }

    const validate = () => {
        const nextErrors = { name: '', mobile: '' };
        const normalizedMobile = mobile.replace(/\D/g, '');

        if (!name.trim()) {
            nextErrors.name = 'Name is required.';
        }
        if (!normalizedMobile || normalizedMobile.length < 8) {
            nextErrors.mobile = 'Enter a valid mobile number.';
        }

        setErrors(nextErrors);
        return !nextErrors.name && !nextErrors.mobile;
    };

    const whatsappTextParts = [
        `Hi, I am interested in this deal: ${listing.title}`,
        `Name: ${name.trim()}`,
        `Mobile Number: ${mobile.trim()}`
    ];

    if (listing.referenceNo) {
        whatsappTextParts.push(`Reference No: ${listing.referenceNo}`);
    }

    const whatsappMessage = encodeURIComponent(whatsappTextParts.join('\n'));
    const whatsappNumber = listing.mobileNumber.replace(/\D/g, '');
    const whatsappHref = whatsappNumber
        ? `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`
        : `https://wa.me/?text=${whatsappMessage}`;

    const handleSend = async (event) => {
        event.preventDefault();
        if (!validate()) {
            return;
        }

        setSubmitError('');
        setIsSubmitting(true);

        const payload = new URLSearchParams({
            name: name.trim(),
            mobile: mobile.trim(),
            pageUrl: `${window.location.origin}/offline-listing/details/${encodeURIComponent(
                listing.id
            )}`
        });

        try {
            await fetch(WEBHOOK_URL, {
                method: 'POST',
                mode: 'no-cors',
                body: payload
            });
            setShowLiveAgentButton(true);
        } catch (error) {
            setSubmitError('Unable to send right now. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (showDesktopPopup) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[rgba(15,23,35,0.92)] px-6 text-center">
                <div className="w-full max-w-[420px] rounded-2xl bg-white px-6 py-7 shadow-[0_14px_30px_rgba(20,38,53,0.2)]">
                    <h2 className="mb-2 text-[26px] font-semibold text-[#243445]">Mobile Only Access</h2>
                    <p className="text-base leading-[1.4] text-[#536172]">
                        This page is available only on mobile devices. Please open it on your phone.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f4f5f7] text-[#1f2937]">
            <div className="mx-auto w-full max-w-[560px] pb-7">
                <header className="flex h-[68px] items-center justify-between border-b border-[#d6dbe1] bg-[#f9f9fa] px-3">
                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/offline-listing/details/${encodeURIComponent(listing.id)}`)
                        }
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#5f6d7c]"
                        aria-label="Back"
                    >
                        <ChevronLeft size={22} />
                    </button>
                    <h1 className="m-0 text-[17px] font-medium uppercase tracking-[0.6px] text-[#1d85d6] sm:text-[20px]">
                        Contact Agent
                    </h1>
                    <span className="inline-flex h-9 w-9" />
                </header>

                <main className="px-3 pt-5">
                    {!showLiveAgentButton && (
                        <section className="rounded-2xl bg-white p-4 shadow-[0_10px_24px_rgba(20,38,53,0.1)]">

                        <form className="mt-5 space-y-4" onSubmit={handleSend}>
                            <div>
                                <label
                                    htmlFor="contact-name"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-[0.4px] text-[#607184]"
                                >
                                    Name
                                </label>
                                <input
                                    id="contact-name"
                                    type="text"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    className="h-11 w-full rounded-xl border border-[#d1d7de] px-3 text-[15px] text-[#1f2937] outline-none focus:border-[#1d85d6]"
                                    placeholder="Enter your name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-xs font-medium text-[#d21d1d]">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="contact-mobile"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-[0.4px] text-[#607184]"
                                >
                                    WhatsApp Number
                                </label>
                                <input
                                    id="contact-mobile"
                                    type="tel"
                                    value={mobile}
                                    onChange={(event) => setMobile(event.target.value)}
                                    className="h-11 w-full rounded-xl border border-[#d1d7de] px-3 text-[15px] text-[#1f2937] outline-none focus:border-[#1d85d6]"
                                    placeholder="Enter your mobile number"
                                />
                                {errors.mobile && (
                                    <p className="mt-1 text-xs font-medium text-[#d21d1d]">{errors.mobile}</p>
                                )}
                            </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex h-[46px] w-full items-center justify-center gap-2 rounded-full bg-[#25d366] text-[14px] font-semibold uppercase tracking-[0.3px] text-white shadow-[0_10px_18px_rgba(24,153,79,0.35)] disabled:opacity-70"
                                >
                                    <Send size={15} className="rotate-45" />
                                    {isSubmitting ? 'Sending...' : 'Send'}
                                </button>
                            {submitError && (
                                <p className="text-center text-xs font-medium text-[#d21d1d]">
                                    {submitError}
                                </p>
                            )}
                        </form>
                        </section>
                    )}

                    {showLiveAgentButton && (
                        <section className="mt-4 overflow-hidden rounded-2xl border border-[#cfe8dc] bg-[#f7faf8] shadow-[0_8px_18px_rgba(20,38,53,0.08)]">
                            <div className="flex items-start justify-between bg-[#0f8f6f] px-4 py-3 text-white">
                                <div className="flex items-center gap-3">
                                    <div className="relative h-12 w-12 shrink-0">
                                        <div className="h-full w-full overflow-hidden rounded-full border-2 border-[rgba(255,255,255,0.65)] bg-[#d6e7df]">
                                            <img
                                                src={agentImage}
                                                alt="Agent"
                                                className="h-full w-full object-cover"
                                            />
                                        </div>
                                        <span className="absolute -bottom-[1px] -right-[1px] flex h-3.5 w-3.5 items-center justify-center">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#59ff96] opacity-80 animate-ping" />
                                            <span className="relative h-3.5 w-3.5 rounded-full border-2 border-[#0f8f6f] bg-[#31d169]" />
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-[18px] font-semibold leading-none">Live Agent</p>
                                        <p className="mt-1 text-[11px] font-semibold uppercase text-[#b7f5cc] animate-pulse">
                                            Active now
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-[#f4f6f5] px-4 py-4">
                                <div className="max-w-[260px] rounded-2xl rounded-tl-sm bg-white px-3 py-2 shadow-[0_4px_10px_rgba(20,38,53,0.08)]">
                                    <p className="text-[14px] font-medium text-[#223546]">Hi there</p>
                                    <div className="mt-1 flex items-end justify-between gap-2">
                                        <p className="text-[14px] text-[#223546]">How can I help you?</p>
                                        <span className="text-[11px] text-[#7f8b95]">Now</span>
                                    </div>
                                </div>

                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-4 flex h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[#25d366] text-[16px] font-semibold text-white shadow-[0_10px_18px_rgba(24,153,79,0.35)]"
                                >
                                    <WhatsAppIcon className="h-[18px] w-[18px]" />
                                    Chat on WhatsApp
                                </a>
                            </div>
                        </section>
                    )}
                </main>
            </div>
        </div>
    );
}

export default OfflineForm;
